/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
    Conversation,
    ConversationsList,
    PagedResponse,
    PagedSortedFilteredRequestParams,
    SortDirection,
} from '@centecomm/portal-service';
import { useConversations } from '@centecomm/portal-service-react';
import { ArrowDownCircle, ArrowUpCircle, IconColors } from 'Icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import RenderIfVisible from 'react-render-if-visible';
import DynamicTranslatedContent from 'Core/i18n/Components/DynamicTranslatedContent';
import { LoadingContent } from 'Shared/PageElements';
import { conversationsActions, conversationsSelectors } from 'State';
import { LoadStatus } from 'Types/AsyncState/LoadStatus';
import styles from './conversations.module.css';

interface ConversationListProps {
    setCurrentConversation: (conversationId: number) => void;
    showActive: boolean;
}

const ScrollButton = ({ scrollDirection }) =>
    scrollDirection === 'down' ? (
        <ArrowDownCircle aspect="3rem" color={IconColors.Action} />
    ) : (
        <ArrowUpCircle aspect="3rem" color={IconColors.Action} />
    );

function ConversationList({
    setCurrentConversation,
    showActive,
}: ConversationListProps): React.ReactElement<ConversationListProps> {
    const { t } = useTranslation(['conversationsFeature']);
    const { getAllConversations } = useConversations();
    const dispatch = useDispatch();
    const conversations: Conversation[] = useSelector(conversationsSelectors.conversationsByActive(showActive));
    const conversationLoadStatus = useSelector(conversationsSelectors.conversationsLoadStatus);
    const [scrollDirection, setScrollDirection] = React.useState<'up' | 'down'>('down');

    const handleScrollButton = () => {
        const conversationsAnchor = document.getElementById('conversations');

        if (conversationsAnchor) {
            if (scrollDirection === 'down' && conversationsAnchor?.scrollHeight) {
                conversationsAnchor.scrollTop = conversationsAnchor?.scrollHeight;

                setScrollDirection('up');
            } else {
                conversationsAnchor.scrollTop = 0;

                setScrollDirection('down');
            }
        }
    };

    useEffect(() => {
        if (conversationLoadStatus === LoadStatus.LOADED) {
            const params = new PagedSortedFilteredRequestParams(
                [
                    {
                        columnName: 'unreadMessages',
                        sortDirection: SortDirection.DESC,
                    },
                    {
                        columnName: 'id',
                        sortDirection: SortDirection.DESC,
                    },
                ],
                [],
                500,
                1,
            );

            getAllConversations(params)
                .then((conversation: PagedResponse<ConversationsList>) => {
                    dispatch({
                        type: conversationsActions.SET_CONVERSATIONS,
                        payload: conversation.results,
                    });
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [conversationLoadStatus]);

    if (!conversations.length) {
        return (
            <div className={styles.LoadingWrapper}>
                <LoadingContent message={t('conversationsFeature:conversationsList.noConversations')} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.ScrollButton} onClick={handleScrollButton}>
                {conversations.length > 10 ? <ScrollButton scrollDirection={scrollDirection} /> : <></>}
            </div>
            <ul className={`${styles.ConversationsList} ${styles.ScrollWrapper}`} id="conversations">
                {conversations.map(({ id, lastMessage, topic, unreadMessages }: Conversation) => (
                    <li
                        key={`conversation-${id}`}
                        className={styles.ConversationItem}
                        onClick={() => {
                            setCurrentConversation(id);
                        }}
                    >
                        <p className={styles.Topic}>
                            <RenderIfVisible defaultHeight={200}>
                                <DynamicTranslatedContent
                                    content={topic}
                                    showLoadingState
                                    extendedOptions={{
                                        bypassMainAppLanguage: true,
                                    }}
                                >
                                    {({ translatedContent }) => <>{translatedContent}</>}
                                </DynamicTranslatedContent>
                            </RenderIfVisible>
                        </p>
                        <p className={styles.Message}>
                            <RenderIfVisible defaultHeight={200}>
                                <DynamicTranslatedContent
                                    content={lastMessage.content}
                                    showLoadingState
                                    extendedOptions={{
                                        bypassMainAppLanguage: true,
                                    }}
                                >
                                    {({ translatedContent }) => <>{translatedContent}</>}
                                </DynamicTranslatedContent>
                            </RenderIfVisible>
                        </p>
                        {unreadMessages > 0 && <p className={styles.UnreadMessageIndicator}>{unreadMessages}</p>}
                    </li>
                ))}
            </ul>
        </>
    );
}

export default ConversationList;
